import React, { useRef, useCallback } from "react";
import { List } from "../../Components/common/List/List";
import { ListProvider } from "../../Components/common/List/Providers/List";
import { useAppContext } from "../../AppProvider";
import { useListProps } from "./utilities/list";
import { Modal, ModalBody } from "reactstrap";
import { useTypes } from "../../utilities/types";
import { createModalConfig, ModalConfig } from "../../Models/ModalConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListTree } from "../../utilities/fontawesomeIcons";
import { useIndexEmailNotificationsContext } from "./providers/Index";
import { EmailNotification } from "../../Models/EmailNotification";
import { getBadge } from "../../Components/Badges/utilities";
import { IndexEmailNotificationEventsProvider } from "../EmailNotificationEvents/providers/Index";
import EmailNotificationEvents from "../EmailNotificationEvents";
import ButtonUtility from "../../Components/Buttons/Utility";
import moment from "moment";
import { Link } from "react-router-dom";

const AnyListProvider = ListProvider as any;
const AnyList = List as any;

export const EmailNotifications: React.FC = () => {
  const { dictionary } = useAppContext();
  const listRef = useRef<{ refresh: () => void } | null>(null);
  const listProps = useListProps();
  const { modalConfig, setModalConfig } = useIndexEmailNotificationsContext();

  const renderItem = useCallback((item: EmailNotification) => {
    return <ListItem item={item} />;
  }, []);

  return (
    <>
      <AnyListProvider
        path={listProps.path}
        filters={listProps.filters}
        sorting={listProps.sorting}
      >
        <AnyList
          ref={listRef}
          title={listProps.title}
          banner={listProps.banner}
          renderItem={renderItem}
          pagination={listProps.pagination}
          selectRowsEnabled={listProps.selectRowsEnabled}
        />
      </AnyListProvider>

      <Modal isOpen={modalConfig.isOpen} size={modalConfig.size}>
        <ModalBody className={modalConfig.className || ""}>
          {modalConfig.action === "events" && (
            <IndexEmailNotificationEventsProvider
              parentId={modalConfig.data?.[0]}
            >
              <EmailNotificationEvents />
              <ButtonUtility
                className="mt-2"
                onClick={() => {
                  listRef.current?.refresh();
                  setModalConfig(createModalConfig());
                }}
              >
                {dictionary.actions.cancel}
              </ButtonUtility>
            </IndexEmailNotificationEventsProvider>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default EmailNotifications;

const ListItem: React.FC<{ item: EmailNotification }> = React.memo(
  ({ item }) => {
    const { dictionary } = useAppContext();
    const context = useIndexEmailNotificationsContext();
    const { triggers, emailEvents } = useTypes();

    const openModal = useCallback(
      (
        action: ModalConfig["action"],
        data?: string[],
        size: ModalConfig["size"] = "sm",
        className = "",
        title = ""
      ) => {
        context?.setModalConfig({
          isOpen: true,
          action,
          size,
          data,
          className,
          title,
        });
      },
      [context]
    );

    const trigger = triggers.find((t) => t.value === item.trigger);
    const lastEvent = emailEvents.find((t) => t.value === item.lastEventType);
    const path =
      trigger?.value.split("_")[0] === "ORDER"
        ? `/orders/details/${item.externalReference}`
        : `/shipments/details/${item.externalReference}`;

    return (
      <div
        className={`list-item ${
          context?.action === "details" && "border"
        } px-2`}
      >
        <div
          className="d-grid grid-column align-items-center"
          style={{
            gridTemplateColumns: "0.5fr 0.75fr 1fr 2fr 1fr 2fr",
          }}
        >
          <div>{getBadge({ bgColor: null, text: lastEvent?.label })}</div>
          <div>
            {getBadge({ bgColor: trigger?.color, text: trigger?.label })}
          </div>
          <div>
            <label className="label-2">{dictionary.dates.creation_date}</label>
            <label className="label-3">
              {item.creationDate
                ? moment(item.creationDate).format("DD/MM/YYYY HH:mm:ss")
                : "-"}
            </label>
          </div>
          <div>
            <label className="label-2">{dictionary.registries.receiver}</label>
            <label className="label-3">{item.recipient || "-"}</label>
          </div>
          <div>
            <label className="label-2">
              {dictionary.references.external_reference}
            </label>
            {item.externalReference ? (
              <Link className="label-3" to={path} target="_blank">
                {item.externalReference}
              </Link>
            ) : (
              "-"
            )}
          </div>
          <div className="text-end">
            <button
              title={dictionary.notifications.event_list}
              className="button-1 button-1-light button-1-icon me-1"
              onClick={() => {
                openModal(
                  "events",
                  [item.id],
                  "lg",
                  "",
                  dictionary.notifications.event_list
                );
              }}
            >
              <FontAwesomeIcon icon={faListTree} />
            </button>
          </div>
        </div>
      </div>
    );
  }
);
